import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { AGE_OPTIONS } from 'constants/search'
import { take } from 'lib/arrayUtils'
import Select from 'components/Common/Form/Input/Select'
import { getChildrenAgesLimits } from 'lib/occupancyUtils'

const AgeSelect = styled(Select)`
  border-radius: ${props => props.theme.borderRadius.medium};
`

interface Props extends Omit<React.ComponentProps<typeof Select>, 'onChange' | 'defaultValue'> {
  childId: number;
  onChange: (childId: number, age: number) => void;
  defaultValue?: number;
  capacities: Array<App.PackageRoomCapacity> | undefined;
  occupancy: App.Occupants;
  maxChildAge: number;
  maxInfantAge: number;
  className?: string;
}

function OccupancyChildAgeSelect(props: Props) {
  const { childId, capacities, occupancy, maxChildAge, maxInfantAge, onChange, defaultValue, className, ...rest } = props
  const [value, setValue] = useState(defaultValue)
  const selectRef = useRef<HTMLSelectElement | null>(null)

  const ageLimits = useMemo(() => (
    getChildrenAgesLimits(capacities, occupancy, childId, maxInfantAge, maxChildAge)
  ), [capacities, occupancy, childId, maxChildAge, maxInfantAge])

  const options = useMemo(() => take(AGE_OPTIONS, ageLimits.max + 1, ageLimits.min), [ageLimits])

  const onSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = parseInt(e.currentTarget.value, 10)
    setValue(selectedValue)
    onChange(childId, selectedValue)
  }, [childId, onChange])

  return (
    <div className={className} style={{ position: 'relative' }}>
      <AgeSelect
        {...rest}
        onChange={onSelectChange}
        value={value}
        noValidationMessage
        noValidationSpacing
        requiredErrorMessage="Please select child's age"
        placeholder="select"
        innerRef={selectRef}
      >
        {options.map(age =>
          <option key={age.value} value={age.value}>{age.label}</option>,
        )}
      </AgeSelect>
    </div>
  )
}

export default OccupancyChildAgeSelect
